import { cva, cx } from 'class-variance-authority';
import { forwardRef } from 'react';
import { Button, type ButtonProps, composeRenderProps } from 'react-aria-components';
import { makeTestID } from 'util/stdtypes';

import styles from './subtle-button.module.css';

const button = cva(styles.button, {
    variants: {
        variant: {
            default: null,
            filled: styles.filled,
            emphasized: styles.emphasized,
        },
        iconOnly: { true: styles.iconOnly },
    },
    defaultVariants: { variant: 'default' },
});

/** @public */
export type SubtleButtonProps = Omit<ButtonProps, 'children'> & {
    icon?: React.ComponentType<{ size?: number; color?: string }>;
    iconPlacement?: 'before' | 'after';
    text?: string;
    /**
     * Foreground color variant matching the design system.
     * `default` uses `colorTextSecondary` (the subtle baseline);
     * `filled` uses `colorTextPrimary`;
     * `emphasized` uses `colorTextAccent`.
     */
    variant?: 'default' | 'filled' | 'emphasized';
    /**
     * Escape hatch: pass any CSS color string to override the computed color.
     * Wins over `variant`. Prefer `variant` for design-system cases.
     */
    colorOverride?: string;
    testID?: string;
};

export const SubtleButton = forwardRef<HTMLButtonElement, SubtleButtonProps>(
    ({ icon: Icon, iconPlacement = 'before', text, variant, colorOverride, testID, className, style, ...rest }, ref) => {
        const iconOnly = Boolean(!text && Icon);
        if (process.env.NODE_ENV !== 'production' && iconOnly && !rest['aria-label']) {
            console.warn('SubtleButton: icon-only usage requires an aria-label.');
        }
        // composeRenderProps: see Dev Notes 'Extending wrapper styles'.
        const composedClassName = composeRenderProps(className, (callerClass) =>
            cx(button({ variant, iconOnly }), 'extendedClickArea', callerClass),
        );
        const composedStyle = colorOverride
            ? composeRenderProps(style, (callerStyle) => ({ ...callerStyle, color: colorOverride }))
            : style;

        return (
            <Button data-testid={makeTestID({ testID, text })} ref={ref} {...rest} className={composedClassName} style={composedStyle}>
                {Icon && iconPlacement === 'before' && <Icon size={16} />}
                {text && <span className={styles.label}>{text}</span>}
                {Icon && iconPlacement === 'after' && <Icon size={16} />}
            </Button>
        );
    },
);
SubtleButton.displayName = 'SubtleButton';
